import { Platform } from 'react-native'

import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import * as Crypto from 'expo-crypto'

import { isIOS, isWebView } from '.'
import { getJfsCustomerId, getJfsCustomerPhoneNumber } from './jfsSession'
import {
  IGetAnalyticsValue,
  IJfsEventParams,
  ISendAnalyticsProps,
} from './types'
import { sendToApp } from './webViewBridge'

const getAnalyticsValues = ({ screen, type }: IGetAnalyticsValue) => {
  const baseConfig: Record<
    IGetAnalyticsValue['screen'],
    { label: string; action: string }
  > = {
    SHOW_PRIMARY_DETAILS_SCREEN_JFS: {
      label: 'User Details',
      action: 'user_detail',
    },
    SHOW_BASIC_DETAILS_SCREEN_JFS: {
      label: 'Basic Details',
      action: 'basic_detail',
    },
    SHOW_PAN_SCREEN_JFS: {
      label: 'PAN Screen',
      action: 'pan_detail',
    },
    SHOW_QUICK_RECOMMENDATION_SCREEN: {
      label: 'Recommendation List',
      action: 'recommendation_list',
    },
    FILTERSCREEN_SCREEN_LOAD: {
      label: 'Filter Screen',
      action: 'filter_load',
    },
    SORTSCREEN_SCREEN_LOAD: {
      label: 'Sort Screen',
      action: 'sort_load',
    },
    FILTERSCREEN_SCREEN_CTA: {
      label: 'Filter Screen',
      action: 'filter_applied',
    },
    SORTSCREEN_SCREEN_CTA: {
      label: 'Sort Screen',
      action: 'sort_applied',
    },
    EDITDETAIL_CTA: {
      label: 'Edit Details Screen',
      action: 'edit_details',
    },
  }

  const config = baseConfig[screen]
  if (!config) return undefined

  const eventSuffix = type === 'screen' ? 'SCREEN_LOAD' : 'SCREEN_CTA'
  const event = `ZET_${config.action.toUpperCase()}_${eventSuffix}`

  return {
    ...config,
    event,
  }
}

const removeNonLatin1Chars = (obj: object): object => {
  const replacer = (value: any): any => {
    if (typeof value === 'string') {
      // Remove characters outside Latin1 (ISO-8859-1)
      return value.replace(/[^\x00-\xFF]/g, '')
    } else if (Array.isArray(value)) {
      return value.map(replacer)
    } else if (typeof value === 'object' && value !== null) {
      return Object.fromEntries(
        Object.entries(value).map(([k, v]) => [k, replacer(v)]),
      )
    }
    return value
  }

  return replacer(obj)
}

const sendAnalyticsData = ({
  screen,
  type,
  optional_data,
}: ISendAnalyticsProps) => {
  const analyticsValues = getAnalyticsValues({ screen, type })
  if (!analyticsValues) return

  const { label, action, event } = analyticsValues

  const cleanedOptionalData = optional_data
    ? JSON.stringify(removeNonLatin1Chars(optional_data))
    : ''

  const analyticsData = {
    type: 'web_ga',
    value: type,
    screenName: label,
    source: !isWebView() ? 'web' : isIOS() ? 'ios' : 'android',
    gaTag: {
      event,
      category: 'Mobile',
      action,
      label,
      general_data: '',
      optional_data: cleanedOptionalData,
      ctEvent: true,
      firebaseEvent: true,
      appsflyerEvent: true,
      clickStreamEvents: true,
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_campaign_id: '',
      ifa: 'ifa',
    },
  }

  if (!isWebView()) {
    triggerJioEvent(event, {
      label,
      ...analyticsData,
    })
  } else {
    sendToApp(analyticsData)
  }
}

const triggerJioEvent = (eventName: string, eventParams = {}) => {
  if (Platform.OS !== 'web') return // 👈 stops crash on native
  ;(window as any).dataLayer = (window as any)?.dataLayer || []
  ;(window as any)?.dataLayer?.push({
    event: eventName,
    ...eventParams,
  })
}

export { getAnalyticsValues, sendAnalyticsData, sendJfsCustomEvent }

// Common JFS GA4 parameters sent with every event; per-event values passed by
// the caller override the defaults. Every sheet parameter is always present
// (empty string when not applicable) so GTM dataLayer variables never carry a
// stale value over from a previous push. sessionStorage is web-only, so the
// session lookups are skipped on native.
const buildJfsEventParams = (eventParams?: IJfsEventParams) => {
  const { customer_id, phone_number, ...restParams } = eventParams || {}
  const storeState = useAddCustomerStore.getState?.()
  return {
    customer_id:
      customer_id ||
      (Platform.OS === 'web' ? getJfsCustomerId() : '') ||
      storeState?.customerId ||
      '',
    phone_number:
      phone_number ||
      (Platform.OS === 'web' ? getJfsCustomerPhoneNumber() : '') ||
      storeState?.customerPhoneNumber ||
      '',
    product_category: 'credit_card',
    platform_name: Platform.OS === 'web' && isWebView() ? 'Jio' : 'Zet',
    click_header: '',
    click_text: '',
    card_name: '',
    pincode: '',
    employment_type: '',
    section_name: '',
    filter_names: '',
    ...restParams,
  }
}

// JFS GA4 events named exactly as in the JFS tracking sheet. They are sent in
// addition to the pre-existing ZET_* events, which stay unchanged.
const sendJfsCustomEvent = (
  eventName:
    | 'enter_user_details'
    | 'enter_work_details'
    | 'enter_pancard_details'
    | 'credit_card_apply_now'
    | 'filter_credit_cards'
    | 'view_credit_card_details'
    | 'edit_info',
  eventParams?: IJfsEventParams,
) => {
  // Fire-and-forget: hashing is async but callers must stay synchronous.
  void (async () => {
    const params = buildJfsEventParams(eventParams)

    // Never send a raw phone number to GA4 — SHA-256 it. When no customer id
    // exists yet (first screen), the hashed phone doubles as the identifier.
    const rawPhone = String(params.phone_number || '')
    params.phone_number = rawPhone ? await hashValueSHA256(rawPhone) : ''
    if (!params.customer_id) {
      params.customer_id = params.phone_number
    }

    if (!isWebView()) {
      triggerJioEvent(eventName, params)
    } else {
      sendToApp({
        type: 'web_ga',
        value: 'click',
        screenName: params.click_header || '',
        source: isIOS() ? 'ios' : 'android',
        ...params,
        gaTag: {
          event: eventName,
          category: 'Mobile',
          action: eventName,
          label: params.click_header || eventName,
          general_data: '',
          optional_data: '',
          ctEvent: true,
          firebaseEvent: true,
          appsflyerEvent: true,
          clickStreamEvents: true,
          utm_source: '',
          utm_medium: '',
          utm_campaign: '',
          utm_campaign_id: '',
          ifa: 'ifa',
        },
      })
    }
  })()
}

const hashValueSHA256 = async (value: string) => {
  try {
    return await Crypto.digestStringAsync(
      Crypto.CryptoDigestAlgorithm.SHA256,
      value,
    )
  } catch {
    return ''
  }
}
